import type { HelpLinkProps } from "../../../../../../components/Questionnaire/Section/Question/HelpLink";
import type { MultiselectGroup } from "../../../../../../components/Questionnaire/Section/Question/types";
import { isQuestion } from "../../../../../../components/Questionnaire/Section/Question/types";
import type { VisibilityMap } from "../../../../../../components/Questionnaire/validation/form/types";
import type { Answers } from "../../../../types";
import type { FlattenGroupResult } from "../types";
import { createEmptyFlattenResult, createShadowQuestion } from "../utils";

/**
 * boolean_group
 * Includes all group questions and a shadow question summarizing visible questions.
 */
export const flattenMultiselectGroup = (
  group: MultiselectGroup,
  answers: Answers,
  visibility: VisibilityMap = {},
): FlattenGroupResult => {
  const {
    id,
    title,
    subText = "",
    groupQuestions = [],
    lastItemDisables,
  } = group;

  const booleanQuestions = groupQuestions.filter(isQuestion);
  const visibleBooleanQuestions = booleanQuestions.filter(q => q.visible);
  // Create help information for visible questions
  const helpText = visibleBooleanQuestions
    .filter(q => q.helpInfo)
    .map(q => `<h3>${q.primaryText}</h3><p>${q.helpInfo?.text}</p>`)
    .join("");
  const helpInfo: HelpLinkProps | undefined = helpText
    ? {
        text: helpText,
        label: "Need help?",
        headline: "More information",
      }
    : undefined;

  if (!booleanQuestions.length) {
    return createEmptyFlattenResult(answers, visibility);
  }

  // Map questions with answers and set visibility to false while preserving includeAnswer
  // For boolean group questions, defaults to "no" if no answer is provided
  const answersByQuestionName = { ...answers };
  const visibilityByQuestionName = { ...visibility };

  const booleanQuestionsWithAnswers = booleanQuestions.map(q => {
    /**
     * Answer resolution priority:
     * 1. Question's own answer (from API/initial state) - q.answer
     * 2. Current form answers state - answers[q.name]
     * 3. Default value "no" (ensures all boolean questions have explicit yes/no values)
     */
    answersByQuestionName[q.name] = q.answer ?? answers[q.name] ?? "no";

    visibilityByQuestionName[q.name] = false;

    // Set includeAnswer to true so these answers are sent to the API even though they're hidden
    return { ...q, includeAnswer: true };
  });

  const shadowQuestion = createShadowQuestion({
    id,
    title,
    subText,
    visibleQuestions: visibleBooleanQuestions,
    answers: answersByQuestionName,
    required: lastItemDisables ?? false,
    lastItemDisables,
    helpInfo,
  });

  return {
    questions: [...booleanQuestionsWithAnswers, shadowQuestion.question],
    groups: {
      [shadowQuestion.question.name]: {
        kind: "shadow",
        names: visibleBooleanQuestions.map(q => q.name),
      },
    },
    visibility: {
      ...visibilityByQuestionName,
      [shadowQuestion.question.name]: true,
    },
    answers: {
      ...answersByQuestionName,
      [shadowQuestion.question.name]: shadowQuestion.answer,
    },
  };
};
